import React, {useEffect, useState} from "react";

const ChooseSolutionV2 = ({
                              solutions,
                              setTotalCredits,
                              selected,
                              setSelected,
                              setSelectedOptions,
                              selectedOptions,
                              setSolutionErrorMessage
                          }) => {
    const [dtcValue, setDtcValue] = useState("");
    const [showDtcOffInput, setShowDtcOffInput] = useState(false);

    useEffect(() => {
        const total = calculateTotalCredits(
            selected.TUNING,
            selected.OPTION
        );

        setTotalCredits(total);
    }, [selected, solutions, setTotalCredits]);

    const handleRadioToggle = (solution) => {
        setSelected((prev) => {
            // if the same tuning is clicked again → deselect
            const newTuning = prev.TUNING === solution.id ? null : solution.id;

            return {...prev, TUNING: newTuning};
        });

        setSelectedOptions((prevSelectedOptions) => {
            const isAlreadySelected = prevSelectedOptions.includes(solution.name);

            // if deselecting the same tuning → remove it
            if (selected.TUNING === solution.id && isAlreadySelected) {
                return prevSelectedOptions.filter((name) => name !== solution.name);
            }

            // remove all other tuning options before adding the new one
            const cleaned = prevSelectedOptions.filter(
                (name) => !solutions.some((sol) => sol.type === "TUNING" && sol.name === name)
            );

            return [...cleaned, solution.name];
        });

        // recalc credits
        const newTuningId = selected.TUNING === solution.id ? null : solution.id;
        calculateTotalCredits(newTuningId, selected.OPTION);
        setSolutionErrorMessage("");
    };

    const handleCheckboxToggle = (solution) => {
        const isSelected = selected.OPTION.includes(solution.id);
        const updatedOptions = isSelected
            ? selected.OPTION.filter((id) => id !== solution.id)
            : [...selected.OPTION, solution.id];

        if (solution.name === "DTC OFF") {
            const isSelected = selectedOptions.includes("DTC OFF");
            if (!isSelected) {
                setShowDtcOffInput(true);
            } else {
                setSelectedOptions((prev) =>
                    prev.filter((option) => !option.startsWith("DTC:"))
                );
                setShowDtcOffInput(false);
            }
        }

        setSelected((prev) => ({...prev, OPTION: updatedOptions}));

        setSelectedOptions((prevSelectedOptions) => {
            if (prevSelectedOptions.includes(solution.name)) {
                return prevSelectedOptions.filter((name) => name !== solution.name);
            } else {
                return [...prevSelectedOptions, solution.name];
            }
        });

        calculateTotalCredits(selected.TUNING, updatedOptions);
        setSolutionErrorMessage("");
    };

    const calculateTotalCredits = (tuningId, optionIds) => {
        const tuningCredits = tuningId
            ? solutions.find((s) => s.id === tuningId)?.credits || 0
            : 0;

        const optionsCredits = optionIds.reduce((acc, id) => {
            const sol = solutions.find((s) => s.id === id);
            return acc + (sol?.credits || 0);
        }, 0);

        return tuningCredits + optionsCredits;
    };

    const handleDtcValue = () => {
        setSelectedOptions((prev) => [...prev, `DTC:${dtcValue}`]);
    };

    const resetForm = () => {
        setSelected({TUNING: null, OPTION: []});
        setSelectedOptions([]);
        setTotalCredits(0);
        setDtcValue("");
        setShowDtcOffInput(false);
    };

    const renderCard = (solution, isActive, toggleFn) => {
        const nameFontClass = solution.name.length > 25 ? "text-[13px]" : "text-[15px]";

        return (
            <div
                key={solution.id}
                onClick={() => toggleFn(solution)}
                className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition
                ${isActive ? "ring-2 ring-red-500" : ""}
                bg-white dark:bg-gray-900
                shadow-md dark:shadow-lg
                hover:bg-gray-100 dark:hover:bg-gray-800
            `}
            >
                {/* Left side: image + text */}
                <div className="flex items-center space-x-4">
                    <img
                        src={solution.image_url}
                        alt={solution.name}
                        className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
                    />
                    <div className="max-w-[150px]">
                        <p className={`font-medium ${nameFontClass} text-gray-900 dark:text-gray-100`}>
                            {solution.name}
                        </p>
                        <p className="text-[12px] text-gray-700 dark:text-gray-300">
                            CRS {solution.credits}
                        </p>
                    </div>
                </div>

                {/* Right side: toggle */}
                <div
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${isActive ? "bg-red-500" : "bg-gray-400 dark:bg-gray-700"}`}
                >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-200 transition-transform
                        ${isActive ? "translate-x-6" : "translate-x-1"}`}
                />
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-10">
            {/* Tuning Options */}
            <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Tuning Options
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {solutions.filter((s) => s.type === "TUNING").length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">No TUNING found.</p>
                    ) : (
                        solutions
                            .filter((s) => s.type === "TUNING")
                            .map((solution) =>
                                renderCard(
                                    solution,
                                    selected.TUNING === solution.id,
                                    handleRadioToggle
                                )
                            )
                    )}
                </div>
            </div>

            {/* Additional Options */}
            <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Additional Options
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {solutions.filter((s) => s.type === "OPTION").length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">No OPTION found.</p>
                    ) : (
                        solutions
                            .filter((s) => s.type === "OPTION")
                            .map((solution) =>
                                renderCard(
                                    solution,
                                    selected.OPTION.includes(solution.id),
                                    handleCheckboxToggle
                                )
                            )
                    )}
                </div>
            </div>

            {/* DTC Input */}
            {showDtcOffInput && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6">
                    <div className="mb-6">
                        <label className="block font-semibold text-gray-900 dark:text-gray-100">
                            Write DTC
                        </label>
                        <input
                            type="text"
                            value={dtcValue}
                            onChange={(e) => setDtcValue(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 w-full p-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-300 transition bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter DTC (P, U) etc..."
                        />
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={() => {
                                handleDtcValue();
                                setShowDtcOffInput(false);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg shadow-lg"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-4">
                <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-500 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg shadow-lg"
                >
                    Reset
                </button>
            </div>
        </div>
    );
};

export default ChooseSolutionV2;